import { useMemo, useState } from 'react';
import type { ProductView } from '../../../../../services/products/products.service';
import {
  EMPTY_PRODUCT_FILTERS,
  filterProducts,
  hasActiveFilters,
  sortProducts,
  type KdsFilter,
  type ProductFilters,
  type ProductSortDir,
  type ProductSortKey,
  type StatusFilter,
} from './product-filter-sort.utils';

type UseProductSettingsListControllerArgs = {
  products: ProductView[];
  categoryNameById: Map<number, string>;
  printerNameById: Map<number, string>;
};

// Architectural summary: owns product-list search/filter/sort state; returns the visible list.
export function useProductSettingsListController({ products, categoryNameById, printerNameById }: UseProductSettingsListControllerArgs) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ProductFilters>(EMPTY_PRODUCT_FILTERS);
  const [sortKey, setSortKey] = useState<ProductSortKey | null>(null);
  const [sortDir, setSortDir] = useState<ProductSortDir>('asc');

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const visibleProducts = useMemo(
    () =>
      sortProducts(
        filterProducts(products, filters, normalizedQuery, categoryNameById),
        sortKey,
        sortDir,
        categoryNameById,
        printerNameById,
      ),
    [products, filters, normalizedQuery, categoryNameById, printerNameById, sortKey, sortDir],
  );

  const setCategoryIds = (categoryIds: number[]) => setFilters((prev) => ({ ...prev, categoryIds }));
  const setPrinterIds = (printerIds: number[]) => setFilters((prev) => ({ ...prev, printerIds }));
  const setKds = (kds: KdsFilter) => setFilters((prev) => ({ ...prev, kds }));
  const setStatus = (status: StatusFilter) => setFilters((prev) => ({ ...prev, status }));

  // Tri-state cycle per column: none -> asc -> desc -> none.
  const toggleSort = (key: ProductSortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    setSortKey(null);
    setSortDir('asc');
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilters(EMPTY_PRODUCT_FILTERS);
  };

  // Reset column sort so the list returns to true display order (re-enables drag-reorder).
  const clearSort = () => {
    setSortKey(null);
    setSortDir('asc');
  };

  return {
    searchQuery,
    setSearchQuery,
    filters,
    setCategoryIds,
    setPrinterIds,
    setKds,
    setStatus,
    sortKey,
    sortDir,
    toggleSort,
    clearSort,
    clearFilters,
    hasActiveFilters: hasActiveFilters(filters, normalizedQuery),
    visibleProducts,
  };
}
